import { Component, OnInit, ViewChild, ElementRef, Input, Output, EventEmitter } from '@angular/core';

import * as d3 from 'd3';
const equal = require('deep-equal');

function formatMoney(amount: any, decimalCount = 2, decimal = '.', thousands = ',') {
  try {
    decimalCount = Math.abs(decimalCount);
    decimalCount = isNaN(decimalCount) ? 2 : decimalCount;

    const negativeSign = amount < 0 ? '-' : '';

    let i = parseInt(amount = Math.abs(Number(amount) || 0)
      .toFixed(decimalCount), 10).toString();
    let j = (i.length > 3) ? i.length % 3 : 0;

    return negativeSign + (j ? i.substr(0, j) + thousands : '') +
      i.substr(j).replace(/(\d{3})(?=\d)/g, '$1' + thousands) +
      (decimalCount ? decimal +
        Math.abs(amount - parseInt(i, 10)).toFixed(decimalCount).slice(2) : '');
  } catch (err) {
    console.log(err);
  }
}

@Component({
  selector: 'dashboard-chart',
  templateUrl: './dashboard-chart.component.html',
  styleUrls: ['./dashboard-chart.component.scss']
})
export class DashboardChartComponent implements OnInit {
  @Input() currentEntity;
  @Input() currentPair;
  @Input() active;
  itemNodes = [];
  graph = {
    links: [],
    nodes: []
  };
  linksElement;
  linksParent;
  linksTextElement;
  private _nodes;
  @Input() nodes;
  nodesElement;
  nodesParent;
  @Output() onChangedNode = new EventEmitter();
  simulation;
  svg;
  timersArray: NodeJS.Timer[] = [];
  isCompAlive = true;
  @ViewChild('svg') svgRef: ElementRef;

  constructor() { }

  ngOnInit() {
  }

  ngOnDestroy() {
    this.clearTimers();
    this.isCompAlive = false;
    try {
      this.simulation.stop();
      this.simulation.on('tick', null);
    } catch (err) {

    }
  }

  ngOnChanges() {
    if (this.nodes && Object.keys(this.nodes).length > 0 &&
      !equal(this.nodes, this._nodes)) {
      this._nodes = this.nodes;
      this.initialize();
    } else if (this.svg && this.nodes && Object.keys(this.nodes).length > 0) {
      this.setActiveNodes();
    }
  }

  clearTimers() {
    this.timersArray.forEach(timer => clearTimeout(timer));
    this.timersArray = [];
  }

  /**
   * Initializes the SVG element.
   */
  initialize() {
    const svg = this.svgRef.nativeElement;
    this.svg = d3.select(svg);
    this.svg.select('.main').selectAll('*').remove();

    this.graph.links = Object.keys(this.nodes).map((name) => {
      const metrics = this.nodes[name].metrics;
      const node = this.nodes[name].entity;
      return {
        source: node.entityId,
        isActive: node.code === this.currentPair.code,
        target: this.currentEntity.entityId,
        metrics,
        value: `${formatMoney(Object.keys(metrics).reduce((acc, metricName) => {
          return acc + metrics[metricName];
        }, 0), 0)}`
      };
    });

    this.graph.nodes = Object.keys(this.nodes).map((name) => {
      const entity = this.nodes[name].entity;
      // TODO: The color is used as a string constant, use variable instead.
      return {
        id: entity.entityId,
        code: entity.code,
        fill: '#655DC6'
      };
    }).concat([{
      id: this.currentEntity.entityId,
      code: this.currentEntity.code,
      // TODO: The color is used as a string constant, use variable instead.
      fill: '#655DC6'
    }]);

    this.initializeNodes();

    // TODO: Radius is a numerical constant, should set somewhere as a variable!
    this.simulation = d3.forceSimulation()
      .force('collision', d3.forceCollide().radius(60))
      .force('center', d3.forceCenter(svg.clientWidth / 2, svg.clientHeight / 2))
      .on('tick', this.tick.bind(this));

    this.simulation
      .nodes(this.graph.nodes);
    this.initializeDragEvents();

    // HACK: This code sucks.
    this.timersArray.push(setTimeout(() => this.setActiveNodes(), 100));
    this.timersArray.push(setTimeout(() => this.setActiveNodes(), 500));
    this.timersArray.push(setTimeout(() => this.setActiveNodes(), 2000));
    this.timersArray.push(setTimeout(() => this.setActiveNodes(), 3500));
  }

  /**
   * Initializes the ability to drag nodes.
   */
  initializeDragEvents() {
    const onDragStart = (context) => {
      if (!d3.event.active) {
        // TODO: AlphaTarget is a numerical constant, should set somewhere as a variable!
        this.simulation.alphaTarget(0.3).restart();
      }

      context.fx = context.x;
      context.fy = context.y;
    };

    const onDrag = (context) => {
      context.fx = d3.event.x;
      context.fy = d3.event.y;
    };

    const onDragEnd = (context) => {
      if (!d3.event.active) {
        this.simulation.alphaTarget(0);
      }

      context.fx = null;
      context.fy = null;
    };

    this.svg.selectAll('.draggable').call(d3.drag()
      .on('start', onDragStart)
      .on('drag', onDrag)
      .on('end', onDragEnd)
    );
  }

  /**
   * This set of adjustments matches the spec to give a bit extra space
   * between the connecting line and nodes.  This function updates the lines
   * connecting the nodes and updates the positions of the nodes.
   */
  tick() {
    if (this.isCompAlive) {
      // gapSize should be the total radius of the node circle plus the gap.
      // This means true gap size is |CIRCLE_RADIUS - gapSize|.
      // TODO: gapSize is a numerical constant, should set somewhere as a variable!
      const gapSize = 45;
      // TODO: Lots of duplicate code here, revise.
      this.linksElement.selectAll('.line-element')
        .attr('x1', link => {
          if (typeof link.source === 'number' || typeof link.target === 'number') {
            return 0;
          };
          const unitVec = Math.sqrt(Math.pow(link.source.x - link.target.x, 2) +
            Math.pow(link.source.y - link.target.y, 2));
          if (isNaN(unitVec) || unitVec === 0) {
            return 0;
          }
          return link.source.x - ((link.source.x - link.target.x) / unitVec) * gapSize;
        })
        .attr('y1', link => {
          if (typeof link.source === 'number' || typeof link.target === 'number') {
            return 0;
          };
          const unitVec = Math.sqrt(Math.pow(link.source.x - link.target.x, 2) +
            Math.pow(link.source.y - link.target.y, 2));
          if (isNaN(unitVec) || unitVec === 0) {
            return 0;
          }
          return link.source.y - ((link.source.y - link.target.y) / unitVec) * gapSize;
        })
        .attr('x2', link => {
          if (typeof link.source === 'number' || typeof link.target === 'number') {
            return 0;
          };
          const unitVec = Math.sqrt(Math.pow(link.source.x - link.target.x, 2) +
            Math.pow(link.source.y - link.target.y, 2));
          if (isNaN(unitVec) || unitVec === 0) {
            return 0;
          }
          return link.target.x - ((link.target.x - link.source.x) / unitVec) * gapSize;
        })
        .attr('y2', link => {
          if (typeof link.source === 'number' || typeof link.target === 'number') {
            return 0;
          };
          const unitVec = Math.sqrt(Math.pow(link.source.x - link.target.x, 2) +
            Math.pow(link.source.y - link.target.y, 2));
          if (isNaN(unitVec) || unitVec === 0) {
            return 0;
          }
          return link.target.y - ((link.target.y - link.source.y) / unitVec) * gapSize;
        });

      this.linksElement.selectAll('.link-label')
        .attr('transform', ({ source, target }) => {
          const xCoord = (source.x + target.x) / 2;
          const yCoord = (source.y + target.y) / 2;
          return `translate(${xCoord || 0}, ${yCoord || 0})`;
        });

      this.nodesElement.attr('transform', ({ x, y }) => 'translate(' + x + ',' +
        y + ')');
    }
  }

  /**
   * Given the root node, initializes all SVG nodes needed for the chart.  This
   * includes nods and links connecting nodes.
   */
  initializeNodes() {
    this.linksElement = this.svg.select('.main')
      .append('g')
      .attr('class', '.link')
      .selectAll('line')
      .data(this.graph.links)
      .enter()
      .append('g')
      .attr('class', 'link');

    this.linksElement
      .append('line')
      .attr('class', 'line-element')
      .attr('stroke-width', '1')
      .attr('stroke', 'lightgray')
      .attr('marker-end', `url(#line-marker)`)
      .attr('marker-start', `url(#line-marker)`);

    const onClicked = this.onClicked.bind(this);
    const group = this.nodesElement = this.svg.select('.main')
      .append('g')
      .attr('class', 'nodes')
      .selectAll('g.nodegroup')
      .data(this.graph.nodes)
      .enter()
      .append('g')
      .attr('class', 'draggable nodegroup')
      .style('cursor', (node) => {
        return this.isActive(node.code) ? 'default' : 'pointer';
      })
      .style('opacity', '0.5')
      .on('click', function (ele) {
        onClicked(ele);
      });

    // TODO: Numeical constants.
    group.append('text')
      .text(({ code }) => code)
      .attr('text-anchor', 'middle')
      .attr('class', 'text')
      .attr('y', -50);
    group.append('circle')
      .attr('r', 38)
      .attr('fill', '#CCEEF1');
    group.append('circle')
      .attr('r', 30)
      .attr('fill', '#C1BEE8');
    group.append('circle')
      .attr('r', 18)
      .attr('fill', '#655DC6');

    const linksText = this.linksElement
      .append('g')
      .attr('class', 'link-label')
      .style('opacity', '0.5')
      .on('click', function (ele) {
        onClicked(ele.source);
      });

    // TODO: Numeical constants.
    linksText.append('rect')
      .style('padding', '12px')
      .style('cursor', 'pointer')
      .attr('rx', '4')
      .attr('ry', '4')
      .attr('x', '-56')
      .attr('y', '-16')
      .attr('width', '112')
      .attr('height', '32')
      .style('fill', '#EFEEF9');

    // TODO: Color constants.
    linksText.append('text')
      .style('font-size', '12px')
      .style('cursor', 'pointer')
      .style('font-weight', '500')
      .attr('y', '4')
      .style('fill', '#212121')
      .attr('text-anchor', 'middle')
      .text(({ value }) => this.roundGraphValues(value));
  }

  roundGraphValues(value) {
    if (!value) {
      return 'N/A';
    }
    let formattedValue: any = Number(Number(value.replace(/,/g, '')).toFixed(2));
    if (isNaN(Number(formattedValue))) {
      return 'N/A';
    }
    if (formattedValue >= 1000000000) {
      formattedValue /= 1000000000;
      formattedValue = Math.round(formattedValue);
      formattedValue += 'B';
    } else if (formattedValue >= 1000000) {
      formattedValue /= 1000000;
      formattedValue = Math.round(formattedValue);
      formattedValue += 'M';
    } else if (formattedValue >= 1000) {
      formattedValue /= 1000;
      formattedValue = Math.round(formattedValue);
      formattedValue += 'K';
    } else {
      formattedValue = Math.round(formattedValue);
    }
    if (Number(formattedValue) === 0) {
      return 'N/A';
    }
    return `${formattedValue}`;
  }

  /**
   * Checks if the provided entity code is active, either in the source or
   * destination entity (within the active pair).
   * @param code
   * @returns {Boolean}
   */
  isActive(code) {
    try {
      return code === this.currentPair.code || code === this.currentEntity.code;
    } catch (err) {
      return false;
    }
  }

  /**
   * Emits the necessary changes so it changes the transaction pair.
   */
  onClicked(ele) {
    this.onChangedNode.emit({ dest: ele.code, src: this.currentEntity.code });
  }

  /**
   * This sets the appropriate coloring to help a user identify the node/link
   * that is active.
   */
  setActiveNodes() {
    // TODO: Color constants.
    // Sets the text to be purple.
    d3.selectAll('.nodegroup .text')
      .attr('style', (node: any) => {
        return this.isActive(node.code) ?
          'font-size: 15px; font-weight: bold; fill: #655DC6' :
          'font-size: 15px; fill: #707983';
      });

    // Sets the opacity of non-active nodes to be translucent to enhance
    // readability.
    d3.selectAll('.nodegroup')
      .style('opacity', (node: any) => {
        return this.isActive(node.code) ?
          '1' :
          '0.5';
      });

    // TODO: Color constants.
    // Sets the line to be purple.
    d3.selectAll('.link .line-element')
      .attr('stroke', (link: any) => {
        return this.isActive(link.source.code) ? '#655DC6' :
          'lightgray';
      });

    // Sets the opacity of nearby labels.
    // TODO: Should we outright remove non-active labels to enhance readability?
    d3.selectAll('.link .link-label')
      .style('opacity', (link: any) => {
        return this.isActive(link.source.code) ? '1' :
          '0.5';
      });

    // TODO: Color constants.
    // Sets the line to be purple.
    d3.selectAll('.link .link-label')
      .select('rect')
      .attr('stroke', (link: any) => {
        return this.isActive(link.source.code) ? '#655DC6' :
          'none';
      });

    // TODO: Color constants.
    // Sets the link text of active node/link to purple.
    d3.selectAll('.link .link-label')
      .select('text')
      .style('fill', (link: any) => {
        return this.isActive(link.source.code) ? '#655DC6' :
          '#212121';
      });

    // TODO: Numeical constants.
    // Sets the repel charge of the active nodes so they have just a bit extra
    // space.
    this.simulation.force('charge', d3.forceManyBody().strength((node: any) => {
      if (this.isActive(node.code)) {
        return -50;
      }
      return 0;
    }));

    // TODO: Numeical constants.
    // Sets the link to be just a bit shorter for active pairs.
    this.simulation.force('link', d3.forceLink(this.graph.links)
      .id((node: any) => node.id).distance((link) => {
        if (this.isActive(link.source.code)) {
          return 200;
        }
        return 240;
      }));

    // Reinforces the center so even when the size of container changes the
    // chart scales with it.
    const svg = this.svgRef.nativeElement;
    this.simulation.force('center', d3.forceCenter(svg.clientWidth / 2,
      svg.clientHeight / 2));

    // Refreshes the chart.
    this.simulation.alpha(0.3).restart();
  }
}
